import React from 'react';
import { Container, Typography, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import SEO from '../components/SEO';

const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '30vh',
  minHeight: '250px',
  color: 'white',
  textAlign: 'center',
  overflow: 'hidden',
  background: 'linear-gradient(45deg, #3c005a 30%, #5c0080 90%)',
  padding: theme.spacing(8, 0),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
}));

const ContentSection = styled(Box)(({ theme }) => ({
  py: theme.spacing(6),
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const privacyStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Privacy Policy - Thrana',
  url: 'https://www.thranatech.com/privacy',
  description: 'Privacy Policy for Thrana Softech Pvt. Ltd. Learn how we collect, use, and protect your information.',
};

const Privacy = () => {
  return (
    <>
      <SEO
        title="Privacy Policy | Thrana"
        description="Privacy Policy for Thrana Softech Pvt. Ltd. Learn how we collect, use, and protect your information."
        path="/privacy"
        structuredData={privacyStructuredData}
      />
      <HeroSection>
        <Container maxWidth="md">
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
              fontWeight: 'bold',
              color: 'white',
            }}
          >
            Privacy Policy
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
              color: 'white',
            }}
          >
            Last Updated: 21-01-2026
          </Typography>
        </Container>
      </HeroSection>

      <ContentSection>
        <Container maxWidth="lg">
          <Typography variant="body1" style={{marginTop:'20px'}} paragraph sx={{ mb: 4 }}>
            This Privacy Policy describes how <strong>Thrana Softech Pvt. Ltd.</strong> ("Thrana", "we", "our", or "us") collects, uses, discloses, and safeguards information when you visit{' '}
            <a href="https://thranatech.com" style={{ color: '#3c005a' }}>https://thranatech.com</a> or engage with our products and services.
          </Typography>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              1. Information We Collect
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ color: '#3c005a', mt: 3, mb: 1 }}>
              1.1 Personal Information
            </Typography>
            <Typography variant="body1" paragraph>
              We may collect the following information when you interact with us:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li><Typography variant="body1">Name, job title, organization name</Typography></li>
              <li><Typography variant="body1">Business email address and phone number</Typography></li>
              <li><Typography variant="body1">Information submitted through contact forms, demo requests, or inquiries</Typography></li>
            </Box>

            <Typography variant="h5" gutterBottom sx={{ color: '#3c005a', mt: 3, mb: 1 }}>
              1.2 Technical & Usage Information
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li><Typography variant="body1">IP address, browser type, operating system</Typography></li>
              <li><Typography variant="body1">Pages visited, access times, referring URLs</Typography></li>
              <li><Typography variant="body1">Cookies and similar tracking technologies</Typography></li>
            </Box>

            <Typography variant="h5" gutterBottom sx={{ color: '#3c005a', mt: 3, mb: 1 }}>
              1.3 Client & Contractual Data
            </Typography>
            <Typography variant="body1" paragraph>
              For customers using our platforms, any data processed is governed by contractual agreements, and Thrana acts as a data processor where applicable.
            </Typography>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              2. How We Use Information
            </Typography>
            <Typography variant="body1" paragraph>
              We use collected information to:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li><Typography variant="body1">Respond to inquiries and provide requested information</Typography></li>
              <li><Typography variant="body1">Deliver, operate, and support our software solutions</Typography></li>
              <li><Typography variant="body1">Improve website performance and user experience</Typography></li>
              <li><Typography variant="body1">Comply with legal, regulatory, and contractual obligations</Typography></li>
              <li><Typography variant="body1">Communicate service updates, security notices, or business information</Typography></li>
            </Box>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              3. Cookies and Tracking Technologies
            </Typography>
            <Typography variant="body1" paragraph>
              Thrana uses cookies to:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li><Typography variant="body1">Ensure proper website functionality</Typography></li>
              <li><Typography variant="body1">Analyze website traffic and performance</Typography></li>
              <li><Typography variant="body1">Improve user experience</Typography></li>
            </Box>
            <Typography variant="body1" paragraph>
              You may disable cookies through your browser settings; however, some website features may be affected. For more detailed information, please see our{' '}
              <a href="/cookies" style={{ color: '#3c005a', textDecoration: 'underline' }}>Cookies Policy</a>.
            </Typography>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              4. Data Sharing and Disclosure
            </Typography>
            <Typography variant="body1" paragraph>
              We do not sell or rent personal information.
            </Typography>
            <Typography variant="body1" paragraph>
              Information may be shared only with:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li><Typography variant="body1">Authorized employees and contractors under confidentiality obligations</Typography></li>
              <li><Typography variant="body1">Trusted service providers (hosting, analytics, communication tools)</Typography></li>
              <li><Typography variant="body1">Regulatory or legal authorities when required by law</Typography></li>
            </Box>
            <Typography variant="body1" paragraph>
              All third parties are contractually bound to protect data confidentiality and security.
            </Typography>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              5. Data Security
            </Typography>
            <Typography variant="body1" paragraph>
              Thrana implements administrative, technical, and organizational safeguards, including:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li><Typography variant="body1">Role-based access controls</Typography></li>
              <li><Typography variant="body1">Encryption of data at rest and in transit (where applicable)</Typography></li>
              <li><Typography variant="body1">Secure cloud infrastructure</Typography></li>
              <li><Typography variant="body1">Audit logging and monitoring</Typography></li>
            </Box>
            <Typography variant="body1" paragraph>
              Despite best practices, no system can be guaranteed 100% secure.
            </Typography>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              6. International Data Transfers
            </Typography>
            <Typography variant="body1" paragraph>
              Where applicable, information may be processed or stored outside your country of residence. Thrana ensures appropriate safeguards consistent with applicable data protection laws.
            </Typography>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              7. Your Rights
            </Typography>
            <Typography variant="body1" paragraph>
              Subject to applicable law, you may:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li><Typography variant="body1">Request access to your personal information</Typography></li>
              <li><Typography variant="body1">Request correction or deletion</Typography></li>
              <li><Typography variant="body1">Withdraw consent where processing is based on consent</Typography></li>
            </Box>
            <Typography variant="body1" paragraph>
              Requests may be sent to: <strong>contact@thranatech.com</strong>
            </Typography>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              8. Third-Party Links
            </Typography>
            <Typography variant="body1" paragraph>
              Our website may contain links to third-party sites. Thrana is not responsible for their privacy practices or content.
            </Typography>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              9. Policy Updates
            </Typography>
            <Typography variant="body1" paragraph>
              This Privacy Policy may be updated periodically. Updates will be posted on this page with a revised <strong>"Last Updated"</strong> date.
            </Typography>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              10. Contact Information
            </Typography>
            <Typography variant="body1" paragraph>
              For privacy-related questions or concerns:
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" paragraph>
                <strong>Thrana Softech Pvt. Ltd.</strong><br />
                <strong>Email:</strong> contact@thranatech.com<br />
                <strong>Website:</strong> <a href="https://thranatech.com" style={{ color: '#3c005a' }}>https://thranatech.com</a>
              </Typography>
            </Box>
          </Section>
        </Container>
      </ContentSection>
    </>
  );
};

export default Privacy;
